import React from 'react';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import QuizAudioPrefsPanel from '@/presentation/quiz/QuizAudioPrefsPanel';
import { useUserWorkspaceStore } from '@/presentation/workspace/useUserWorkspaceStore';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

export default function SettingsManagementPage({ session }) {
  const { t } = useAppTranslation();
  const currentTheme = useUserWorkspaceStore((s) => s.theme);
  const setTheme = useUserWorkspaceStore((s) => s.setTheme);
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [draftTheme, setDraftTheme] = React.useState(currentTheme);
  const [result, setResult] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setDraftTheme(currentTheme);
  }, [currentTheme]);

  React.useEffect(() => {
    let mounted = true;
    if (!token) return undefined;
    api
      .getSettings(token)
      .then((data) => {
        if (!mounted) return;
        const serverTheme = data?.theme === 'dark' ? 'dark' : 'light';
        setDraftTheme(serverTheme);
        setTheme(serverTheme);
      })
      .catch((e) => setResult(formatApiErrorMessage(e?.message, t) || t('errors.loadSettings')));
    return () => {
      mounted = false;
    };
  }, [api, token, setTheme, t]);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <h1 className="font-headline text-3xl font-extrabold">{t('settings.pageTitle')}</h1>
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <h2 className="text-lg font-bold">{t('settings.themeHeading')}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('settings.themeIntro')}</p>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => setDraftTheme('light')}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${draftTheme === 'light' ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-100'}`}
          >
            {t('settings.themeLightBtn')}
          </button>
          <button
            onClick={() => setDraftTheme('dark')}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${draftTheme === 'dark' ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-100'}`}
          >
            {t('settings.themeDarkBtn')}
          </button>
          <button
            onClick={async () => {
              if (!token) return;
              setLoading(true);
              try {
                const data = await api.updateSettings({ theme: draftTheme }, token);
                const applied = data?.theme === 'dark' ? 'dark' : 'light';
                setTheme(applied);
                setResult(t('settings.themeApplied', { theme: applied }));
              } catch (e) {
                setResult(formatApiErrorMessage(e?.message, t) || t('settings.applyFailed'));
              } finally {
                setLoading(false);
              }
            }}
            className="rounded-lg border border-cyan-300 px-4 py-2 text-sm font-bold text-cyan-700"
          >
            {loading ? t('settings.applying') : t('settings.apply')}
          </button>
        </div>
        {result ? <p className="mt-3 text-sm font-semibold text-emerald-700">{result}</p> : null}
      </div>

      <QuizAudioPrefsPanel id="dashboard-quiz-audio" />
    </div>
  );
}

